import React, { useCallback, useEffect, useState } from 'react';
import {
  BookOpen, Plus, Trash2, Check, Lock, Play, Loader2,
  ChevronUp, ChevronDown, ChevronRight, Pencil, Copy, UserCheck, Undo2, X,
  SlidersHorizontal, Video, Sparkles, Users, User,
} from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type {
  Curriculum, CurriculumModule, GettingStarted, QuizContent, QuizContentQuestion,
  ReadingSection, VideoLibrary, AaiVideo, Workspace,
} from '../../../types/aai';
import { Loading, ErrorNote } from '../components';
import { Card, MonoLabel, initialsOf } from '../dashboards/ui';

const STAGES = ['nascent', 'early', 'developing', 'mature', 'advanced'];

const contentOf = (m: CurriculumModule): { kind?: string } & Record<string, unknown> =>
  (m.content as { kind?: string } & Record<string, unknown>) || {};
const kindOf = (m: CurriculumModule): string => contentOf(m).kind || '';

// Trainer Framework §3 — teaching style + delivery + the artifact kind.
const METHODS = [
  { value: '', label: 'No specific style' },
  { value: 'explainer', label: 'Plain explainer' },
  { value: 'story', label: 'Story-based' },
  { value: 'analogy', label: 'Analogy / connection' },
];
const DELIVERY = [
  { value: '', label: 'Either' },
  { value: 'group', label: 'Group' },
  { value: 'solo', label: '1:1 / solo' },
];
const STEP_KINDS = [
  { value: '', label: 'None', hint: 'A placeholder — participants won’t see it yet.' },
  { value: 'reading', label: 'Reading', hint: 'Short explainer sections.' },
  { value: 'micro_learning', label: 'Micro-learning', hint: 'A quick, evergreen read.' },
  { value: 'quiz', label: 'Quiz', hint: 'A scored knowledge check.' },
  { value: 'homework', label: 'Homework', hint: 'A self-directed task with a submission.' },
  { value: 'link', label: 'Platform activity', hint: 'Send them into a real feature.' },
];
// Participant-facing feature anchors a 'link' step can point at (mirrors feature_links).
const FEATURE_KEYS = [
  { key: 'onboarder', label: 'Onboarder' },
  { key: 'tool_library', label: 'Tool Library' },
  { key: 'tool_dives', label: 'Tool Dives' },
  { key: 'whats_allowed', label: "What's Allowed" },
  { key: 'skill_builder', label: 'Skill Builder — context' },
  { key: 'skill_builder_workflow', label: 'Skill Builder — workflow' },
  { key: 'skill_versioning', label: 'Vetting' },
  { key: 'skill_graduation', label: 'Share a skill' },
  { key: 'org_chart', label: 'Org Chart' },
  { key: 'my_impact', label: 'My Productivity' },
  { key: 'certification', label: 'AA Certification' },
];
const KIND_BADGE: Record<string, string> = {
  quiz: 'Quiz', reading: 'Reading', micro_learning: 'Micro', homework: 'Homework', link: 'Activity',
};
const METHOD_SHORT: Record<string, string> = { explainer: 'Explainer', story: 'Story', analogy: 'Analogy' };

const STATUS_META: Record<string, { label: string; cls: string; icon: React.ElementType }> = {
  locked: { label: 'Locked', cls: 'bg-gray-100 text-gray-500', icon: Lock },
  active: { label: 'Active', cls: 'bg-brand-50 text-brand-700', icon: Play },
  complete: { label: 'Complete', cls: 'bg-emerald-50 text-emerald-700', icon: Check },
};
const SESSION_TYPES = ['coaching', 'quick-win', 'deep-dive'];
const NEXT_STATUS: Record<string, { to: string; label: string }> = {
  locked: { to: 'active', label: 'Activate' },
  active: { to: 'complete', label: 'Mark complete' },
  complete: { to: 'active', label: 'Reopen' },
};

function reordered(ids: string[], from: number, dir: number): string[] | null {
  const to = from + dir;
  if (to < 0 || to >= ids.length) return null;
  const next = ids.slice();
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

const CurriculumPage: React.FC = () => {
  const [cur, setCur] = useState<Curriculum | null>(null);
  const [gs, setGs] = useState<GettingStarted | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState('coaching');
  const [editPhase, setEditPhase] = useState<string | null>(null);
  const [editPhaseTitle, setEditPhaseTitle] = useState('');
  const [addingPhase, setAddingPhase] = useState(false);
  const [newPhaseTitle, setNewPhaseTitle] = useState('');
  const [editModule, setEditModule] = useState<string | null>(null);
  const [editModuleTitle, setEditModuleTitle] = useState('');
  const [editModuleType, setEditModuleType] = useState('coaching');
  // step (content) editor — one modal for every kind
  const [stepModule, setStepModule] = useState<CurriculumModule | null>(null);
  const [editName, setEditName] = useState(false);
  const [nameVal, setNameVal] = useState('');
  const [savingTpl, setSavingTpl] = useState(false);
  const [tplName, setTplName] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  // Multi-org context: the client workspaces this trainer spans (design's
  // "PROGRAM FOR" bar). Switching re-scopes every AAI call via setAaiCompany.
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeCompany, setActiveCompany] = useState<string | null>(null);
  // Per-module (phase) expand/collapse — modules aren't reorderable, they fold.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleCollapse = (id: string) => setCollapsed((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  // DEL-1: Orbit invites each enrolled participant to their next lesson.
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState<string | null>(null);
  const sendInvites = async () => {
    setInviting(true);
    setInviteResult(null);
    try {
      const { data } = await aaiApi.inviteParticipants();
      setInviteResult(`Invited ${data.invited} participant${data.invited === 1 ? '' : 's'} to their next lesson${data.skipped ? `, skipped ${data.skipped} already invited or done` : ''}.`);
    } catch {
      setInviteResult(null);
      setError('Could not send the invites.');
    } finally { setInviting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, g] = await Promise.all([aaiApi.getCurriculum(), aaiApi.getGettingStarted()]);
      setCur(c.data); setGs(g.data); setError(null);
    } catch { setError('Could not load the program.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Load the trainer's client workspaces + the active one, once.
  useEffect(() => {
    (async () => {
      try {
        const [ws, ctx] = await Promise.all([aaiApi.getWorkspaces(), aaiApi.getContext()]);
        setWorkspaces(ws.data.workspaces || []);
        setActiveCompany(ctx.data.company_id);
      } catch { /* the bar is optional — page works without it */ }
    })();
  }, []);

  const switchClient = (id: string) => {
    if (id === activeCompany) return;
    aaiApi.setAaiCompany(id);
    setActiveCompany(id);
    load();
  };

  const run = async (fn: () => Promise<Curriculum>) => {
    setBusy(true);
    try { setCur(await fn()); }
    finally { setBusy(false); }
  };

  const chooseMethod = async (choice: 'aa_standard' | 'custom') => {
    if (choice === 'custom' && cur && cur.phases.length > 0 &&
        !window.confirm('Build your own method? This clears the current modules so you start from a blank canvas. Tip: “Save as template” first to keep this program — you can re-apply it anytime from the Templates tab.')) return;
    if (choice === 'aa_standard' && cur && cur.phases.length > 0 && cur.preset !== 'aa_standard' &&
        !window.confirm('Start from the AA Standard? This replaces your current program with the 6-module program. Tip: “Save as template” first to keep this one — you can re-apply it anytime from the Templates tab.')) return;
    await run(async () => (await aaiApi.setupProgram(choice)).data);
  };

  const saveName = async () => {
    if (!nameVal.trim()) return;
    await run(async () => (await aaiApi.renameProgram(nameVal.trim())).data);
    setEditName(false);
  };
  const toggleHandoff = async () => {
    if (!cur) return;
    const to = cur.managed_by !== 'champion';
    await run(async () => (await aaiApi.setHandoff(to)).data);
  };
  const saveTemplate = async () => {
    if (!tplName.trim()) return;
    setBusy(true);
    try {
      await aaiApi.saveAsTemplate({ name: tplName.trim() });
      setNotice(`Saved “${tplName.trim()}” as a reusable template — apply it to any client from their Program settings.`);
      setSavingTpl(false); setTplName('');
    } finally { setBusy(false); }
  };

  const advance = (phaseId: string, to: string) => run(async () => (await aaiApi.patchPhase(phaseId, { status: to })).data);
  const renamePhase = async (id: string) => {
    if (!editPhaseTitle.trim()) return;
    await run(async () => (await aaiApi.patchPhase(id, { title: editPhaseTitle.trim() })).data);
    setEditPhase(null);
  };
  const removePhase = async (id: string) => {
    if (!window.confirm('Remove this module and its lessons? This only affects this client.')) return;
    await run(async () => (await aaiApi.deletePhase(id)).data);
  };
  const addPhase = async () => {
    if (!newPhaseTitle.trim()) return;
    await run(async () => (await aaiApi.createPhase({ title: newPhaseTitle.trim() })).data);
    setNewPhaseTitle(''); setAddingPhase(false);
  };
  // Modules (phases) are no longer reorderable by design — no movePhase.

  const addModule = async (phaseId: string) => {
    if (!newTitle.trim()) return;
    setBusy(true);
    try { await aaiApi.addModule(phaseId, { title: newTitle.trim(), session_type: newType }); setNewTitle(''); setAddingTo(null); await load(); }
    finally { setBusy(false); }
  };
  const removeModule = async (id: string) => {
    setBusy(true);
    try { await aaiApi.deleteModule(id); await load(); }
    finally { setBusy(false); }
  };
  const saveModule = async (id: string) => {
    if (!editModuleTitle.trim()) return;
    setBusy(true);
    try {
      await aaiApi.patchModule(id, { title: editModuleTitle.trim(), session_type: editModuleType });
      setEditModule(null); await load();
    } finally { setBusy(false); }
  };
  const moveModule = (phaseId: string, mods: CurriculumModule[], index: number, dir: number) => {
    const next = reordered(mods.map((m) => m.id), index, dir);
    if (next) run(async () => (await aaiApi.reorderModules(phaseId, next)).data);
  };

  if (loading) return <Loading label="Loading the program…" />;
  if (error || !cur) return <ErrorNote message={error || 'Something went wrong.'} />;

  const handedOff = cur.managed_by === 'champion';

  // Program stats strip (design trainer_home) — derived only from the real
  // program tree loaded above. Phases + modules are direct counts; "Active" and
  // "Complete" come from real phase.status (the same states shown per-phase).
  // The design's "Cohorts running" and "Completion %" have no data source on
  // this page, so they're omitted (no fabricated numbers).
  const moduleCount = cur.phases.reduce((n, p) => n + p.modules.length, 0);
  const programStats = [
    { v: cur.phases.length, l: 'Modules' },
    { v: moduleCount, l: 'Lessons' },
    { v: cur.phases.filter((p) => p.status === 'active').length, l: 'Active' },
    { v: cur.phases.filter((p) => p.status === 'complete').length, l: 'Complete' },
  ];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <BookOpen size={22} className="text-brand-500" />
        <h1 className="text-2xl font-semibold text-gray-900">Program</h1>
        <span className="flex-1" />
        {/* DEL-1: no scheduled workshops — Orbit invites each participant to
            their next lesson individually, in-app + email. */}
        <button disabled={busy || inviting} onClick={sendInvites}
          className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 text-brand-700 text-[12.5px] font-semibold px-3.5 py-1.5 hover:bg-brand-100 disabled:opacity-50 whitespace-nowrap">
          {inviting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Invite participants
        </button>
      </div>
      {inviteResult && <p className="text-[12px] text-emerald-600 mb-2">{inviteResult}</p>}
      <p className="text-sm text-gray-500 mb-5">Your curriculum — name it, reorder or edit modules and their lessons, and hand it to the client's champion when they're ready to run it themselves. This is the program your dashboards and each participant's journey read.</p>

      {/* Multi-org context bar — the clients this trainer spans (design's
          "PROGRAM FOR"). Only shown when there's more than one to switch between. */}
      {workspaces.length > 0 && (
        <div className="flex items-center gap-2.5 flex-wrap mb-5">
          <span className="font-sans text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400">Program for</span>
          {workspaces.map((w) => {
            const label = w.display_name || w.name || 'Workspace';
            const active = w.id === activeCompany;
            return (
              <button key={w.id} onClick={() => switchClient(w.id)} disabled={busy}
                className={`whitespace-nowrap inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12.5px] font-semibold transition-colors disabled:opacity-50 ${active ? 'bg-brand-500 border-brand-500 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-brand-200'}`}>
                <span className={`grid place-items-center w-[18px] h-[18px] rounded-[5px] text-[9px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>{initialsOf(label)}</span>
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* First-run: choose the method (AA Standard vs build your own) */}
      {cur.setup_pending && (
        <Card className="mb-4 border-brand-200 ring-1 ring-brand-100">
          <div className="flex items-center gap-2 mb-1"><Sparkles size={16} className="text-brand-500" /><MonoLabel>Choose your method</MonoLabel></div>
          <p className="text-[12.5px] text-gray-500 mb-3">Start from the AA Standard (the flagship 6-module program) or build your own from a blank canvas. You can change this any time — structure, timeline and tiers are always enforced; the content is yours.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <button disabled={busy} onClick={() => chooseMethod('aa_standard')}
              className="text-left rounded-lg border border-brand-200 bg-brand-50 hover:border-brand-300 px-3.5 py-3 disabled:opacity-50">
              <div className="font-semibold text-[13.5px] text-gray-900">Start from AA Standard <span className="text-[11px] font-semibold text-brand-600">Recommended</span></div>
              <div className="text-[12px] text-gray-500 mt-0.5">6 modules, tier-ready, with quizzes and hands-on lessons seeded.</div>
            </button>
            <button disabled={busy} onClick={() => chooseMethod('custom')}
              className="text-left rounded-lg border border-gray-200 bg-white hover:border-gray-300 px-3.5 py-3 disabled:opacity-50">
              <div className="font-semibold text-[13.5px] text-gray-900">Build my own method</div>
              <div className="text-[12px] text-gray-500 mt-0.5">A blank program with one starter module to build from.</div>
            </button>
          </div>
        </Card>
      )}

      {/* Program header: name + handoff + save-as-template */}
      <Card className="mb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <MonoLabel className="mb-1">Program name</MonoLabel>
            {editName ? (
              <span className="flex items-center gap-1.5">
                <input autoFocus value={nameVal} onChange={(e) => setNameVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditName(false); }}
                  className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-[15px] font-semibold w-64 focus:outline-none focus:ring-2 focus:ring-brand-200" />
                <button disabled={busy} onClick={saveName} className="text-emerald-600 disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={16} />}</button>
                <button onClick={() => setEditName(false)} className="text-[12px] text-gray-400">Cancel</button>
              </span>
            ) : (
              <button onClick={() => { setEditName(true); setNameVal(cur.name); }}
                className="group inline-flex items-center gap-1.5 text-lg font-semibold text-gray-900 hover:text-brand-600">
                {cur.name}<Pencil size={13} className="text-gray-300 group-hover:text-brand-500" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {cur.preset === 'custom' && <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold bg-gray-100 text-gray-600">Custom method</span>}
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${handedOff ? 'bg-emerald-50 text-emerald-700' : 'bg-brand-50 text-brand-700'}`}>
              <UserCheck size={12} />{handedOff ? 'Champion-run' : 'Trainer-run'}
            </span>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-4 flex-wrap">
          <button disabled={busy} onClick={toggleHandoff}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-gray-600 hover:text-brand-700 disabled:opacity-50">
            {handedOff ? <><Undo2 size={14} /> Take the program back</> : <><UserCheck size={14} /> Hand off to the champion</>}
          </button>
          {savingTpl ? (
            <span className="flex items-center gap-1.5">
              <input autoFocus value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Template name"
                onKeyDown={(e) => { if (e.key === 'Enter') saveTemplate(); if (e.key === 'Escape') setSavingTpl(false); }}
                className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12.5px] w-48 focus:outline-none focus:ring-2 focus:ring-brand-200" />
              <button disabled={busy || !tplName.trim()} onClick={saveTemplate} className="text-[12px] font-semibold bg-brand-500 text-white px-2.5 py-1.5 rounded-lg disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : 'Save'}</button>
              <button onClick={() => setSavingTpl(false)} className="text-[12px] text-gray-400">Cancel</button>
            </span>
          ) : (
            <button onClick={() => { setSavingTpl(true); setTplName(cur.name); }}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-gray-600 hover:text-brand-700">
              <Copy size={14} /> Save as template
            </button>
          )}
          {!cur.setup_pending && (
            <button disabled={busy} onClick={() => chooseMethod(cur.preset === 'custom' ? 'aa_standard' : 'custom')}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-gray-500 hover:text-brand-700 disabled:opacity-50">
              <Sparkles size={14} /> {cur.preset === 'custom' ? 'Reset to AA Standard' : 'Build my own method'}
            </button>
          )}
        </div>
        {notice && <p className="mt-2.5 text-[12px] text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">{notice}</p>}
      </Card>

      {gs && gs.complete < gs.total && (
        <Card className="mb-4">
          <MonoLabel className="mb-3">Getting started · {gs.complete}/{gs.total}</MonoLabel>
          <div className="space-y-1.5">
            {gs.steps.map((s) => (
              <div key={s.key} className="flex items-center gap-2.5 text-[13px]">
                {s.done ? <Check size={15} className="text-emerald-500" /> : <span className="w-[15px] h-[15px] rounded-full border border-gray-300 inline-block" />}
                <span className={s.done ? 'text-gray-500 line-through' : 'text-gray-800'}>{s.label}</span>
                <span className="text-[11.5px] text-gray-400 ml-auto">{s.detail}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Modules & sessions header with a compact, fully-derived stats strip */}
      {cur.phases.length > 0 && (
        <div className="flex items-center justify-between mt-1 mb-3 gap-2 flex-wrap">
          <span className="font-semibold text-[15px] text-gray-900">Modules &amp; lessons</span>
          <div className="flex items-center gap-1">
            {programStats.map((s) => (
              <span key={s.l} className="text-[11.5px] text-gray-400 px-2">
                <b className="text-gray-900 font-semibold">{s.v}</b> {s.l}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        {cur.phases.map((ph) => {
          const meta = STATUS_META[ph.status] || STATUS_META.locked;
          const StatusIcon = meta.icon;
          const next = NEXT_STATUS[ph.status];
          const isCollapsed = collapsed.has(ph.id);
          return (
            <div key={ph.id} className={`bg-white border border-gray-100 rounded-xl p-5 ${!ph.unlocked && ph.status === 'locked' ? 'opacity-70' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <button onClick={() => toggleCollapse(ph.id)} title={isCollapsed ? 'Expand' : 'Collapse'}
                    className="text-gray-400 hover:text-brand-600 shrink-0">
                    {isCollapsed ? <ChevronRight size={17} /> : <ChevronDown size={17} />}
                  </button>
                  {editPhase === ph.id ? (
                    <span className="flex items-center gap-1.5">
                      <input autoFocus value={editPhaseTitle} onChange={(e) => setEditPhaseTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') renamePhase(ph.id); if (e.key === 'Escape') setEditPhase(null); }}
                        className="rounded-lg border border-gray-200 px-2 py-1 text-[14px] font-semibold w-44 focus:outline-none focus:ring-2 focus:ring-brand-200" />
                      <button disabled={busy} onClick={() => renamePhase(ph.id)} className="text-emerald-600 disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={15} />}</button>
                      <button onClick={() => setEditPhase(null)} className="text-[12px] text-gray-400">Cancel</button>
                    </span>
                  ) : (
                    <span className="font-semibold text-[15px] truncate">{ph.title}</span>
                  )}
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}><StatusIcon size={11} />{meta.label}</span>
                </div>
                {editPhase !== ph.id && (
                  <div className="flex items-center gap-2 shrink-0">
                    {next && (
                      <button disabled={busy} onClick={() => advance(ph.id, next.to)}
                        className="text-[12px] font-semibold text-gray-500 hover:text-brand-700 disabled:opacity-50">{next.label}</button>
                    )}
                    <button onClick={() => { setEditPhase(ph.id); setEditPhaseTitle(ph.title); }}
                      className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-brand-600 bg-brand-50 hover:bg-brand-100 rounded-lg px-3 py-1.5 whitespace-nowrap"><Pencil size={13} /> Edit</button>
                    <button disabled={busy} onClick={() => removePhase(ph.id)} title="Delete module"
                      className="text-gray-300 hover:text-red-500 disabled:opacity-50"><Trash2 size={15} /></button>
                  </div>
                )}
              </div>
              {!isCollapsed && (<>
              {ph.description && <p className="text-[12.5px] text-gray-500 mb-3 mt-2">{ph.description}</p>}

              <div className="space-y-1.5">
                {ph.modules.map((m, mIdx) => (
                  <div key={m.id} className="flex items-center gap-2.5 bg-gray-50 rounded-lg px-3 py-2">
                    <span className="flex flex-col -my-1">
                      <button disabled={busy || mIdx === 0} onClick={() => moveModule(ph.id, ph.modules, mIdx, -1)} title="Move up" className="text-gray-300 hover:text-brand-600 disabled:opacity-30 leading-none"><ChevronUp size={13} /></button>
                      <button disabled={busy || mIdx === ph.modules.length - 1} onClick={() => moveModule(ph.id, ph.modules, mIdx, 1)} title="Move down" className="text-gray-300 hover:text-brand-600 disabled:opacity-30 leading-none"><ChevronDown size={13} /></button>
                    </span>
                    {editModule === m.id ? (
                      <>
                        <input autoFocus value={editModuleTitle} onChange={(e) => setEditModuleTitle(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveModule(m.id); if (e.key === 'Escape') setEditModule(null); }}
                          className="flex-1 rounded-lg border border-gray-200 px-2 py-1 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
                        <select value={editModuleType} onChange={(e) => setEditModuleType(e.target.value)} className="rounded-lg border border-gray-200 px-1.5 py-1 text-[11.5px]">
                          {SESSION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <button disabled={busy} onClick={() => saveModule(m.id)} className="text-emerald-600 disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />}</button>
                        <button onClick={() => setEditModule(null)} className="text-[11.5px] text-gray-400">Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => { setEditModule(m.id); setEditModuleTitle(m.title); setEditModuleType(m.session_type); }}
                          title="Rename / lesson type" className="text-[12.5px] font-medium flex-1 text-left hover:text-brand-600 min-w-0">
                          <span className="block truncate">{m.title}</span>
                          {/* FND-1: the lesson → artifact contract, visible where the trainer curates.
                              A lesson with no artifact reads as what it is: decorative. */}
                          <span className={`block truncate text-[10.5px] ${m.artifact ? 'text-gray-400' : 'text-amber-500/80'}`}>
                            {m.artifact ? `Leaves behind: ${m.artifact}` : 'Leaves nothing behind — decorative'}
                          </span>
                        </button>
                        <ModuleBadges m={m} />
                        <span className="text-[10.5px] font-sans uppercase tracking-wide text-gray-400 hidden sm:inline">{m.session_type}</span>
                        <button disabled={busy} onClick={() => setStepModule(m)} title="Edit lesson content"
                          className={`${kindOf(m) ? 'text-brand-500' : 'text-gray-300'} hover:text-brand-600 disabled:opacity-50`}><SlidersHorizontal size={14} /></button>
                        <button disabled={busy} onClick={() => removeModule(m.id)} className="text-gray-300 hover:text-red-500 disabled:opacity-50"><Trash2 size={14} /></button>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {addingTo === ph.id ? (
                <div className="mt-2.5 flex items-center gap-2">
                  <input autoFocus value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Lesson title"
                    className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
                  <select value={newType} onChange={(e) => setNewType(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1.5 text-[12.5px]">
                    {SESSION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <button disabled={busy || !newTitle.trim()} onClick={() => addModule(ph.id)} className="text-[12px] font-semibold bg-brand-500 text-white px-3 py-1.5 rounded-lg disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : 'Add'}</button>
                  <button onClick={() => { setAddingTo(null); setNewTitle(''); }} className="text-[12px] text-gray-500">Cancel</button>
                </div>
              ) : (
                <button onClick={() => { setAddingTo(ph.id); setNewTitle(''); }} className="mt-2.5 inline-flex items-center gap-1 text-[12px] font-medium text-gray-500 hover:text-brand-600"><Plus size={13} /> Add lesson</button>
              )}
              </>)}
            </div>
          );
        })}

        {addingPhase ? (
          <div className="flex items-center gap-2 bg-white border border-dashed border-gray-300 rounded-xl p-4">
            <input autoFocus value={newPhaseTitle} onChange={(e) => setNewPhaseTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addPhase(); }}
              placeholder="New module name (e.g. Foundations)"
              className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
            <button disabled={busy || !newPhaseTitle.trim()} onClick={addPhase}
              className="text-[12px] font-semibold bg-brand-500 text-white px-3 py-2 rounded-lg disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : 'Add module'}</button>
            <button onClick={() => { setAddingPhase(false); setNewPhaseTitle(''); }} className="text-[12px] text-gray-500">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setAddingPhase(true)}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-600 hover:text-brand-700"><Plus size={14} /> Add module</button>
        )}
      </div>

      {stepModule && (
        <StepEditor module={stepModule} onClose={() => setStepModule(null)}
          onSaved={async () => { setStepModule(null); await load(); }} />
      )}
    </div>
  );
};

// Small read-only chips on a module row: kind, teaching style, delivery.
const ModuleBadges: React.FC<{ m: CurriculumModule }> = ({ m }) => {
  const kind = kindOf(m);
  const qCount = kind === 'quiz' ? ((contentOf(m).questions as unknown[])?.length ?? 0) : 0;
  const hasVideo = !!contentOf(m).video_ref;
  return (
    <span className="flex items-center gap-1 shrink-0">
      {kind && <span className="rounded-full bg-brand-50 text-brand-600 px-1.5 py-0.5 text-[10px] font-semibold">{KIND_BADGE[kind] || kind}{qCount ? ` · ${qCount}Q` : ''}</span>}
      {hasVideo && <Video size={12} className="text-gray-400" aria-label="has video" />}
      {m.delivery_mode && (m.delivery_mode === 'group'
        ? <Users size={12} className="text-gray-400" aria-label="group" />
        : <User size={12} className="text-gray-400" aria-label="solo" />)}
      {m.method && <span className="rounded-full bg-gray-100 text-gray-500 px-1.5 py-0.5 text-[10px] font-medium hidden sm:inline">{METHOD_SHORT[m.method]}</span>}
    </span>
  );
};

// ── Step editor — one modal that authors every kind + method/delivery + video ──
const emptyQuestion = (): QuizContentQuestion => ({ q: '', options: ['', ''], answer: 0 });
const emptySection = (): ReadingSection => ({ h: '', p: '' });

const StepEditor: React.FC<{
  module: CurriculumModule;
  onClose: () => void;
  onSaved: () => Promise<void>;
}> = ({ module, onClose, onSaved }) => {
  const c = contentOf(module);
  const [kind, setKind] = useState<string>(kindOf(module));
  const [method, setMethod] = useState<string>(module.method || '');
  const [delivery, setDelivery] = useState<string>(module.delivery_mode || '');
  // reading / micro-learning
  const [sections, setSections] = useState<ReadingSection[]>(
    Array.isArray(c.body) && (c.body as ReadingSection[]).length ? (c.body as ReadingSection[]).map((s) => ({ ...s })) : [emptySection()],
  );
  // link
  const [featureKey, setFeatureKey] = useState<string>((c.feature_key as string) || FEATURE_KEYS[0].key);
  // homework
  const [instructions, setInstructions] = useState<string>((c.instructions as string) || '');
  const [submitType, setSubmitType] = useState<string>((c.submit_type as string) || 'text');
  // quiz
  const existingQuiz = kindOf(module) === 'quiz' ? (module.content as unknown as QuizContent) : null;
  const [passPct, setPassPct] = useState(Math.round((existingQuiz?.pass_score ?? 0.7) * 100));
  const [stages, setStages] = useState<string[]>(existingQuiz?.stages ?? []);
  const [questions, setQuestions] = useState<QuizContentQuestion[]>(
    existingQuiz?.questions?.length ? existingQuiz.questions.map((q) => ({ ...q, options: [...q.options] })) : [emptyQuestion()],
  );
  // video
  const [videoRef, setVideoRef] = useState<string>((c.video_ref as string) || '');
  const [videos, setVideos] = useState<VideoLibrary | null>(null);
  const [addingVideo, setAddingVideo] = useState(false);
  const [vTitle, setVTitle] = useState('');
  const [vUrl, setVUrl] = useState('');
  // SKL-8: the marketplace skill this lesson starts from ("customise this
  // existing skill"). Kept in state so a save never wipes a stored ref.
  const [skillRef, setSkillRef] = useState<string>((c.skill_ref as string) || '');
  const [marketSkills, setMarketSkills] = useState<Array<{ id: string; name: string; published: boolean }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { aaiApi.listVideos().then((r) => setVideos(r.data)).catch(() => setVideos({ standard: [], client: [] })); }, []);
  useEffect(() => {
    // Company-scope only: a team-scoped starter would silently fail to prefill
    // for every participant outside that department.
    aaiApi.listSkills()
      .then((r) => setMarketSkills(((r.data as any).skills || []).filter((s: any) => s.published && s.scope === 'company')))
      .catch(() => setMarketSkills([]));
  }, []);

  const setQ = (i: number, patch: Partial<QuizContentQuestion>) =>
    setQuestions((qs) => qs.map((q, qi) => (qi === i ? { ...q, ...patch } : q)));
  const setOption = (i: number, oi: number, value: string) =>
    setQ(i, { options: questions[i].options.map((o, j) => (j === oi ? value : o)) });
  const toggleStage = (s: string) => setStages((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  const setSection = (i: number, patch: Partial<ReadingSection>) =>
    setSections((ss) => ss.map((s, si) => (si === i ? { ...s, ...patch } : s)));

  const quizValid = passPct > 0 && passPct <= 100 && questions.length > 0 && questions.every(
    (q) => q.q.trim() && q.options.length >= 2 && q.options.every((o) => o.trim()) && q.answer >= 0 && q.answer < q.options.length);
  const sectionsValid = sections.some((s) => s.h.trim() || s.p.trim());
  const valid = kind === '' ? true
    : kind === 'quiz' ? quizValid
    : kind === 'link' ? !!featureKey
    : kind === 'homework' ? instructions.trim().length > 0
    : sectionsValid; // reading / micro_learning

  const buildContent = (): Record<string, unknown> => {
    const body = () => sections.filter((s) => s.h.trim() || s.p.trim()).map((s) => ({ h: s.h.trim(), p: s.p.trim() }));
    let content: Record<string, unknown> = {};
    if (kind === 'quiz') content = { kind, pass_score: passPct / 100, ...(stages.length ? { stages } : {}), questions };
    else if (kind === 'reading' || kind === 'micro_learning') content = { kind, body: body() };
    else if (kind === 'link') content = { kind, feature_key: featureKey };
    else if (kind === 'homework') content = { kind, instructions: instructions.trim(), submit_type: submitType };
    if (kind && videoRef) content.video_ref = videoRef;
    if (kind && skillRef) content.skill_ref = skillRef;
    return content;
  };

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await aaiApi.patchModule(module.id, { content: buildContent(), method, delivery_mode: delivery });
      await onSaved();
    } catch {
      setError('Could not save — check the fields for this lesson type are complete.');
      setSaving(false);
    }
  };

  const addVideo = async () => {
    if (!vTitle.trim() || !vUrl.trim()) return;
    try {
      const { data } = await aaiApi.createVideo({ title: vTitle.trim(), url: vUrl.trim() });
      setVideos((v) => v ? { ...v, client: [data, ...v.client] } : { standard: [], client: [data] });
      setVideoRef(data.id); setAddingVideo(false); setVTitle(''); setVUrl('');
    } catch { setError('Could not add that video.'); }
  };

  const allVideos: AaiVideo[] = [...(videos?.standard ?? []), ...(videos?.client ?? [])];

  return (
    <div className="fixed inset-0 z-[90] bg-black/30 flex items-start justify-center overflow-y-auto py-8 px-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={18} className="text-brand-500" />
            <h2 className="text-[16px] font-semibold text-gray-900">Lesson content · {module.title}</h2>
          </div>
          <button onClick={onClose} title="Close" className="text-gray-400 hover:text-gray-600"><X size={17} /></button>
        </div>
        <p className="text-[12px] text-gray-500 mb-4">How this lesson is taught and what the participant does. Structure and timeline stay enforced — the content is yours.</p>

        {/* Teaching style + delivery */}
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <label className="flex items-center gap-1.5 text-[12.5px] text-gray-600 font-medium">Teaching style
            <select value={method} onChange={(e) => setMethod(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1 text-[12.5px]">
              {METHODS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-[12.5px] text-gray-600 font-medium">Delivery
            <select value={delivery} onChange={(e) => setDelivery(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1 text-[12.5px]">
              {DELIVERY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        </div>

        {/* Step type */}
        <MonoLabel className="mb-2">Lesson type</MonoLabel>
        <div className="flex flex-wrap gap-1.5 mb-1">
          {STEP_KINDS.map((k) => (
            <button key={k.value} onClick={() => setKind(k.value)}
              className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${kind === k.value ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {k.label}
            </button>
          ))}
        </div>
        <p className="text-[11.5px] text-gray-400 mb-4">{STEP_KINDS.find((k) => k.value === kind)?.hint}</p>

        {/* Per-kind body */}
        {(kind === 'reading' || kind === 'micro_learning') && (
          <div className="space-y-2.5 mb-4">
            {sections.map((s, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <input value={s.h} onChange={(e) => setSection(i, { h: e.target.value })} placeholder="Section heading"
                    className="flex-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12.5px] font-semibold focus:outline-none focus:ring-2 focus:ring-brand-200" />
                  <button disabled={sections.length <= 1} onClick={() => setSections((ss) => ss.filter((_, j) => j !== i))}
                    title="Remove section" className="text-gray-300 hover:text-red-500 disabled:opacity-30"><Trash2 size={14} /></button>
                </div>
                <textarea value={s.p} onChange={(e) => setSection(i, { p: e.target.value })} placeholder="Section text" rows={2}
                  className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
              </div>
            ))}
            <button onClick={() => setSections((ss) => [...ss, emptySection()])}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600 hover:text-brand-700"><Plus size={13} /> Add section</button>
          </div>
        )}

        {kind === 'link' && (
          <label className="block mb-4 text-[12.5px] text-gray-600 font-medium">Sends the participant to
            <select value={featureKey} onChange={(e) => setFeatureKey(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12.5px]">
              {FEATURE_KEYS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </label>
        )}

        {kind === 'homework' && (
          <div className="space-y-2.5 mb-4">
            <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={3} placeholder="What should the participant do?"
              className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
            <label className="flex items-center gap-2 text-[12.5px] text-gray-600 font-medium">Submission
              <select value={submitType} onChange={(e) => setSubmitType(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1 text-[12.5px]">
                <option value="text">Text answer</option>
                <option value="link">A link</option>
                <option value="none">Just mark done</option>
              </select>
            </label>
          </div>
        )}

        {kind === 'quiz' && (
          <div className="mb-4">
            <div className="flex items-center gap-5 flex-wrap mb-3">
              <label className="flex items-center gap-2 text-[12.5px] text-gray-600 font-medium">Pass score
                <input type="number" min={1} max={100} value={passPct} title="Pass score (%)"
                  onChange={(e) => setPassPct(Number(e.target.value))}
                  className="w-16 rounded-lg border border-gray-200 px-2 py-1 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-200" /> %
              </label>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[12.5px] text-gray-600 font-medium">Recommended for</span>
                {STAGES.map((s) => (
                  <button key={s} onClick={() => toggleStage(s)}
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${stages.includes(s) ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>{s}</button>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              {questions.map((q, qi) => (
                <div key={qi} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] font-sans text-gray-400 shrink-0">Q{qi + 1}</span>
                    <input value={q.q} onChange={(e) => setQ(qi, { q: e.target.value })} placeholder="Question text"
                      className="flex-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
                    <button disabled={questions.length <= 1} onClick={() => setQuestions((qs) => qs.filter((_, j) => j !== qi))}
                      title="Remove question" className="text-gray-300 hover:text-red-500 disabled:opacity-30"><Trash2 size={14} /></button>
                  </div>
                  <div className="space-y-1.5 pl-6">
                    {q.options.map((o, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <button onClick={() => setQ(qi, { answer: oi })} title="Mark as the correct answer"
                          className={`h-4 w-4 rounded-full border grid place-items-center shrink-0 ${q.answer === oi ? 'border-emerald-500' : 'border-gray-300 hover:border-gray-400'}`}>
                          {q.answer === oi && <span className="h-2 w-2 rounded-full bg-emerald-500" />}
                        </button>
                        <input value={o} onChange={(e) => setOption(qi, oi, e.target.value)} placeholder={`Option ${oi + 1}`}
                          className="flex-1 rounded-lg border border-gray-200 px-2.5 py-1 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
                        <button disabled={q.options.length <= 2} onClick={() => setQ(qi, {
                          options: q.options.filter((_, j) => j !== oi),
                          answer: q.answer === oi ? 0 : q.answer > oi ? q.answer - 1 : q.answer,
                        })} title="Remove option" className="text-gray-300 hover:text-red-500 disabled:opacity-30"><X size={13} /></button>
                      </div>
                    ))}
                    <button onClick={() => setQ(qi, { options: [...q.options, ''] })} className="inline-flex items-center gap-1 text-[11.5px] font-medium text-gray-500 hover:text-brand-600"><Plus size={12} /> Add option</button>
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setQuestions((qs) => [...qs, emptyQuestion()])}
              className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-600 hover:text-brand-700"><Plus size={13} /> Add question</button>
          </div>
        )}

        {/* Video (optional, any kind) */}
        {kind !== '' && (
          <div className="mb-2">
            <MonoLabel className="mb-1.5">Video (optional)</MonoLabel>
            <div className="flex items-center gap-2">
              <select value={videoRef} onChange={(e) => setVideoRef(e.target.value)} className="flex-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12.5px]">
                <option value="">No video</option>
                {(videos?.standard.length ?? 0) > 0 && (
                  <optgroup label="Standard library">{videos!.standard.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}</optgroup>
                )}
                {(videos?.client.length ?? 0) > 0 && (
                  <optgroup label="Your recordings">{videos!.client.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}</optgroup>
                )}
              </select>
              <button onClick={() => setAddingVideo((a) => !a)} className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600 hover:text-brand-700"><Plus size={13} /> URL</button>
            </div>
            {addingVideo && (
              <div className="mt-2 flex items-center gap-2">
                <input value={vTitle} onChange={(e) => setVTitle(e.target.value)} placeholder="Video title"
                  className="w-40 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
                <input value={vUrl} onChange={(e) => setVUrl(e.target.value)} placeholder="https://…"
                  className="flex-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
                <button disabled={!vTitle.trim() || !vUrl.trim()} onClick={addVideo} className="text-[12px] font-semibold bg-brand-500 text-white px-2.5 py-1.5 rounded-lg disabled:opacity-50">Add</button>
              </div>
            )}
            {videoRef && <p className="text-[11px] text-gray-400 mt-1">Plays above the lesson for participants{allVideos.find((v) => v.id === videoRef)?.scope === 'standard' ? ' · standard library' : ''}.</p>}
          </div>
        )}

        {/* SKL-8: starter skill (optional, any kind — most useful on the two
            skill-builder activities). The participant opens the builder
            pre-seeded from this real skill: "customise this", not a blank box. */}
        {kind && (marketSkills.length > 0 || skillRef) && (
          <div className="mt-4">
            <MonoLabel className="mb-1.5">Starter skill (optional)</MonoLabel>
            <select value={skillRef} onChange={(e) => setSkillRef(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12.5px]">
              <option value="">No starter — they compose from their own draft</option>
              {skillRef && !marketSkills.some((s) => s.id === skillRef) &&
                <option value={skillRef}>Current starter — no longer available (unpublished or removed)</option>}
              {marketSkills.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {skillRef && marketSkills.some((s) => s.id === skillRef) &&
              <p className="text-[11px] text-gray-400 mt-1">The lesson shows "Starts from a real skill" and opens the builder pre-filled from it.</p>}
            {skillRef && !marketSkills.some((s) => s.id === skillRef) &&
              <p className="text-[11px] text-amber-500/90 mt-1">This starter is no longer a published company skill — participants get a normal blank build. Pick another or set "No starter".</p>}
          </div>
        )}

        {error && <div className="mt-3"><ErrorNote message={error} /></div>}
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onClose} className="text-[12.5px] text-gray-500 px-3 py-2">Cancel</button>
          <button disabled={saving || !valid} onClick={save}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[12.5px] font-semibold px-4 py-2 whitespace-nowrap">
            {saving && <Loader2 size={13} className="animate-spin" />} Save lesson
          </button>
        </div>
      </div>
    </div>
  );
};

export default CurriculumPage;
